import React from 'react';
import { Modal, Typography, Descriptions, Table, Tag, Space, Alert, Empty, Spin } from 'antd';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useBlockchainHealthStatus } from '../../hooks/useIndexerManagement';
import { formatDistanceToNow } from 'date-fns';
import HealthStatusTag from './HealthStatusTag';

// Define types for health status data
interface ApiMetric {
  totalCalls: number;
  successfulCalls: number;
  failedCalls: number;
  lastSuccess?: number;
  lastFailure?: number;
  avgResponseTime: number;
}

interface ErrorRecord {
  message: string;
  timestamp: number;
  endpoint?: string;
  key?: number; // For table key
}

interface BlockchainHealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy' | 'stopped' | 'unknown' | 'error';
  successRate?: number;
  lastUpdated?: number;
  recentErrors?: ErrorRecord[];
  apiMetrics?: Record<string, ApiMetric>;
  consecutiveFailures?: number;
  running?: boolean;
  error?: string | null;
}

interface HealthStatusResponse {
  status: string;
  overallStatus?: string;
  timestamp: string;
  indexers?: Record<string, BlockchainHealthStatus>;
  blockchain?: string;
  healthStatus?: BlockchainHealthStatus;
  message?: string;
}

const { Title, Text } = Typography;

interface HealthStatusDetailModalProps {
  blockchain: string;
  visible: boolean;
  onClose: () => void;
}

/**
 * Modal component to display detailed health status information for a blockchain indexer
 */
const HealthStatusDetailModal: React.FC<HealthStatusDetailModalProps> = ({
  blockchain,
  visible,
  onClose
}) => {
  const { t: indexerT } = useTranslationWithNamespace('indexer');
  const {
    data: healthData,
    isLoading,
    isError
  } = useBlockchainHealthStatus(blockchain, visible ? 10000 : 0) as {
    data: HealthStatusResponse | undefined;
    isLoading: boolean;
    isError: boolean;
  };

  // Format timestamp to relative time
  const formatTimestamp = (timestamp: number) => {
    if (!timestamp) return 'N/A';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Columns for recent errors table
  const errorColumns = [
    {
      title: indexerT('health.errorMessage', 'Error Message'),
      dataIndex: 'message',
      key: 'message',
      render: (text: string) => <Text ellipsis={{ tooltip: text }}>{text}</Text>
    },
    {
      title: indexerT('health.endpoint', 'Endpoint'),
      dataIndex: 'endpoint',
      key: 'endpoint'
    },
    {
      title: indexerT('health.timestamp', 'Time'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: number) => formatTimestamp(timestamp)
    }
  ];

  // Columns for API metrics table
  const apiMetricsColumns = [
    {
      title: indexerT('health.endpoint', 'Endpoint'),
      dataIndex: 'endpoint',
      key: 'endpoint'
    },
    {
      title: indexerT('health.totalCalls', 'Total Calls'),
      dataIndex: 'totalCalls',
      key: 'totalCalls'
    },
    {
      title: indexerT('health.successRate', 'Success Rate'),
      dataIndex: 'successRate',
      key: 'successRate',
      render: (rate: number) => (
        <Tag color={rate > 90 ? 'green' : rate > 70 ? 'orange' : 'red'}>
          {`${Math.round(rate)}%`}
        </Tag>
      )
    },
    {
      title: indexerT('health.avgResponseTime', 'Avg Response Time'),
      dataIndex: 'avgResponseTime',
      key: 'avgResponseTime',
      render: (time: number) => `${Math.round(time)}ms`
    },
    {
      title: indexerT('health.lastSuccess', 'Last Success'),
      dataIndex: 'lastSuccess',
      key: 'lastSuccess',
      render: (timestamp: number) => formatTimestamp(timestamp)
    },
    {
      title: indexerT('health.lastFailure', 'Last Failure'),
      dataIndex: 'lastFailure',
      key: 'lastFailure',
      render: (timestamp: number) => timestamp ? formatTimestamp(timestamp) : 'N/A'
    }
  ];

  // Helper function to get health data for the blockchain
  const getHealthData = () => {
    // Check if data is in the format from the health-status endpoint (with indexers object)
    if (healthData?.indexers?.[blockchain]) {
      return healthData.indexers[blockchain];
    }

    // Check if data is in the format from the blockchain-specific health endpoint
    if (healthData?.healthStatus) {
      return healthData.healthStatus;
    }

    // No health data available
    return null;
  };

  // Transform API metrics object to array for table
  const getApiMetricsData = () => {
    // Get health data for the current blockchain
    const blockchainHealthData = getHealthData();

    // Check if apiMetrics exists and is not undefined
    const apiMetrics = blockchainHealthData?.apiMetrics;
    if (!apiMetrics) return [];

    // Now TypeScript knows apiMetrics is not undefined
    return Object.entries(apiMetrics).map(([endpoint, metrics]) => {
      // Cast the metrics to ApiMetric type
      const typedMetrics = metrics as ApiMetric;
      return {
        endpoint,
        totalCalls: typedMetrics.totalCalls,
        successRate: (typedMetrics.successfulCalls / typedMetrics.totalCalls) * 100,
        avgResponseTime: typedMetrics.avgResponseTime,
        lastSuccess: typedMetrics.lastSuccess,
        lastFailure: typedMetrics.lastFailure
      };
    });
  };

  // Get health data for the current blockchain
  const blockchainHealthData = getHealthData();

  return (
    <Modal
      title={
        <Space>
          <Title level={4}>{indexerT('health.detailTitle', '{{blockchain}} Health Status', { blockchain: blockchain.toUpperCase() })}</Title>
          {blockchainHealthData && (
            <HealthStatusTag
              status={blockchainHealthData.status}
              successRate={blockchainHealthData.successRate}
            />
          )}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      ) : isError ? (
        <Alert
          type="error"
          message={indexerT('health.errorTitle', 'Error Loading Health Status')}
          description={indexerT('health.errorDescription', 'Failed to load health status information. Please try again.')}
        />
      ) : !blockchainHealthData ? (
        <Empty description={indexerT('health.noData', 'No health data available')} />
      ) : (
        <>
          <Descriptions title={indexerT('health.overview', 'Overview')} bordered column={2}>
            <Descriptions.Item label={indexerT('health.status', 'Status')}>
              <HealthStatusTag
                status={blockchainHealthData.status}
                successRate={blockchainHealthData.successRate}
              />
            </Descriptions.Item>
            <Descriptions.Item label={indexerT('health.running', 'Running')}>
              {blockchainHealthData.running ?
                <Tag color="green">Yes</Tag> :
                <Tag color="red">No</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label={indexerT('health.successRate', 'Success Rate')}>
              {blockchainHealthData.successRate !== undefined ?
                `${Math.round(blockchainHealthData.successRate as number)}%` :
                'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label={indexerT('health.lastUpdated', 'Last Updated')}>
              {formatTimestamp(blockchainHealthData.lastUpdated || 0)}
            </Descriptions.Item>
            <Descriptions.Item label={indexerT('health.consecutiveFailures', 'Consecutive Failures')}>
              {blockchainHealthData.consecutiveFailures || 0}
            </Descriptions.Item>
            {blockchainHealthData.error && (
              <Descriptions.Item label={indexerT('health.errorMessage', 'Error Message')} span={2}>
                <Alert type="error" message={blockchainHealthData.error} />
              </Descriptions.Item>
            )}
          </Descriptions>

          <Title level={5} style={{ marginTop: 20 }}>
            {indexerT('health.recentErrors', 'Recent Errors')}
          </Title>
          {(blockchainHealthData.recentErrors || []).length > 0 ? (
            <Table
              dataSource={(blockchainHealthData.recentErrors || []).map((error: ErrorRecord, index: number) => ({ ...error, key: index }))}
              columns={errorColumns}
              pagination={false}
              size="small"
            />
          ) : (
            <Empty description={indexerT('health.noErrors', 'No recent errors')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}

          <Title level={5} style={{ marginTop: 20 }}>
            {indexerT('health.apiMetrics', 'API Metrics')}
          </Title>
          <Table
            dataSource={getApiMetricsData()}
            columns={apiMetricsColumns}
            pagination={false}
            size="small"
          />
        </>
      )}
    </Modal>
  );
};

export default HealthStatusDetailModal;
